'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import {
  useFetchLeagues,
  useFetchLeagueStatsLeaders,
  useFetchPlayerStatMatches,
} from '@/lib/api/league-routes'
import type { League, StatMatch, StatsLeader } from '@/lib/types/leagues'
import { abbreviatePosition, cn } from '@/lib/utils'

const YEAR_OPTIONS = Array.from({ length: 2026 - 1992 + 1 }, (_, i) => 2026 - i)
const DEFAULT_SEASON = 2025
const DEFAULT_LEAGUE_ID = 2
const PINNED_LEAGUE_IDS = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 202, 24, 99, 66, 33, 102, 302, 402, 502]
const LIMIT = 12

// Synthetic multi-league selections — negative sentinels so they can't
// collide with a real league_id. Each resolves to a fixed league_ids list.
const TOP5_LEAGUE_ID = -5
const TOP5_LEAGUE_IDS = [1, 2, 3, 4, 5]

const ALL_EURO_LEAGUE_ID = -7
const ALL_EURO_LEAGUE_IDS = [
  1, 2, 3, 4, 5, 7, 8, 10, 11, 12, 13, 108, 208, 308, 408, 508, 708, 808, 1108, 1008, 207, 99, 66, 33,
]

type StatOption = { value: string; label: string }

const STATS: StatOption[] = [
  { value: 'goals', label: 'Goals' },
  { value: 'goals_assists', label: 'Goal Involvements' },
  { value: 'assists', label: 'Assists' },
  { value: 'xg', label: 'xG' },
  { value: 'xg_assists', label: 'xA' },
  { value: 'xga', label: 'xGA' },
  { value: 'goals_p90', label: 'Goals per 90' },
  { value: 'goals_assists_p90', label: 'Goal Involvements per 90' },
  { value: 'assists_p90', label: 'Assists per 90' },
  { value: 'shots', label: 'Shots' },
  { value: 'shot_accuracy', label: 'Shot Accuracy' },
  { value: 'headed_shots', label: 'Headed Shots' },
  { value: 'big_chances_created', label: 'Chances Created' },
  { value: 'succ_dribbles', label: 'Dribbles Completed' },
  { value: 'pens_made', label: 'Penalty Goals' },
  { value: 'touches', label: 'Touches' },
  { value: 'touches_att_pen_area', label: 'Touches in Attacking Box' },
  { value: 'touches_per_goal', label: 'Touches per Goal' },
  { value: 'passes_completed', label: 'Passes Completed' },
  { value: 'passes_completed_in_final_third', label: 'Passes Completed in Final Third' },
  { value: 'tackles_won', label: 'Tackles Won' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'interceptions', label: 'Interceptions' },
  { value: 'defensive_contributions', label: 'Defensive Contributions' },
  { value: 'errors_to_shot_goal', label: 'Errors Leading to Goal/Shot' },
  { value: 'dispossessed', label: 'Dispossessed' },
  { value: 'sprints', label: 'Sprints' },
  { value: 'sprinting_distance', label: 'Sprinting Distance' },
  { value: 'running_distance', label: 'Running Distance' },
  { value: 'distance_covered', label: 'Distance Covered' },
]

const DECIMAL_STATS = new Set([
  'xg', 'xg_assists', 'xga', 'goals_p90', 'goals_assists_p90', 'assists_p90', 'touches_per_goal',
])
const PERCENT_STATS = new Set(['shot_accuracy'])
const DISTANCE_STATS = new Set(['sprinting_distance', 'running_distance', 'distance_covered'])

// These stats get the per-match bar chart in the accordion (line up with
// STATS_LEADERS_CHART_STATS on the API).
const CHART_STATS = new Set([
  'goals', 'assists', 'goals_assists', 'succ_dribbles', 'big_chances_created', 'shots',
  'xg', 'xg_assists', 'xga', 'touches', 'passes_completed',
])

function formatBarValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

// Mirrors the position filter row on /fpl (basic-fpl-stats.tsx) — the values
// map onto the backend's POSITION_GROUPS or an exact players.position.
const POSITION_FILTERS: { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'Goalkeeper', label: 'GK' },
  { value: 'defender', label: 'DEF' },
  { value: 'midfielder', label: 'MID' },
  { value: 'forwards', label: 'FWD' },
]

// Max-age pills — the value is passed as ?age= and the API keeps players
// that age or younger.
const AGE_FILTERS: { value: number | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 19, label: 'U19' },
  { value: 20, label: 'U20' },
  { value: 21, label: 'U21' },
  { value: 22, label: 'U22' },
]

const OUTCOME_CLASSES: Record<'win' | 'loss' | 'draw', string> = {
  win: 'bg-green-500/25 border-green-500',
  loss: 'bg-red-500/25 border-red-500',
  draw: 'bg-yellow-500/25 border-yellow-500',
}

function formatValue(stat: string, value: number | null | undefined): string {
  if (value == null) return '—'
  if (PERCENT_STATS.has(stat)) return `${value.toFixed(1)}%`
  if (DISTANCE_STATS.has(stat)) return `${Math.round(value).toLocaleString()}m`
  if (DECIMAL_STATS.has(stat)) return value.toFixed(2)
  return value.toLocaleString()
}

// "2026-09-08" -> "9/8". Parsed by hand rather than via Date to keep it
// timezone-proof (the API sends a plain date string).
function formatMonthDay(dateStr: string): string {
  const parts = dateStr.slice(0, 10).split('-')
  if (parts.length !== 3) return dateStr
  return `${Number(parts[1])}/${Number(parts[2])}`
}

// Long names get squeezed hard in the row's fixed-width name column, so
// anything 12 chars or longer is shortened to "F. Lastname".
function displayName(name: string): string {
  if (name.length < 12) return name
  const parts = name.trim().split(/\s+/)
  if (parts.length < 2) return name
  return `${parts[0][0]}. ${parts[parts.length - 1]}`
}

function LeagueOptionInner({ league }: { league: League }) {
  return (
    <>
      {league.country?.flag_url ? (
        <img src={league.country.flag_url} alt="" className="h-[12px] w-[18px] shrink-0 rounded-[1px] object-cover" />
      ) : (
        <div className="h-[12px] w-[18px] shrink-0 rounded-[1px] bg-muted" />
      )}
      {league.logo_url && <img src={league.logo_url} alt="" className="size-4 shrink-0 object-contain" />}
      <span className="truncate">{league.recent_competition_name ?? league.league_name}</span>
    </>
  )
}

function LeagueSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const { data, isLoading } = useFetchLeagues()
  const leagues = useMemo<League[]>(() => {
    const all = data?.data ?? []
    return PINNED_LEAGUE_IDS
      .map((id) => all.find((l) => l.league_id === id))
      .filter((l): l is League => l !== undefined)
  }, [data])

  if (isLoading) return <Skeleton className="h-9 w-full" />

  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-[220px] overflow-y-auto">
        <SelectItem value={String(TOP5_LEAGUE_ID)}>
          <span className="text-xs">🌍</span>
          <span className="truncate">Top 5 Leagues</span>
        </SelectItem>
        <SelectItem value={String(ALL_EURO_LEAGUE_ID)}>
          <span className="text-xs">🇪🇺</span>
          <span className="truncate">All European Comps</span>
        </SelectItem>
        {leagues.map((league) => (
          <SelectItem key={league.league_id} value={String(league.league_id)}>
            <LeagueOptionInner league={league} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function LeadersSkeleton() {
  return (
    <div>
      {Array.from({ length: LIMIT }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 border-b border-white/10 px-2 py-2 last:border-b-0 sm:gap-3">
          <Skeleton className="h-2.5 w-4 shrink-0" />
          <Skeleton className="size-4 shrink-0" />
          <Skeleton className="h-3 w-32 shrink-0 sm:w-40" />
          <Skeleton className="h-1.5 min-w-0 flex-1 rounded-full" />
          <Skeleton className="h-3 w-8 shrink-0" />
        </div>
      ))}
    </div>
  )
}

function LeaderRowContent({ leader, rank, stat, maxValue, delayMs }: { leader: StatsLeader; rank: number; stat: string; maxValue: number; delayMs: number }) {
  const value = leader.stat_value ?? 0
  const barWidth = Math.max(4, (Math.abs(value) / maxValue) * 70)
  const barColor = leader.team?.color_hex || '#ffffff'

  return (
    <div
      style={{ animationDelay: `${delayMs}ms`, animationFillMode: 'backwards' }}
      className="flex w-full animate-in items-center gap-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out sm:gap-3"
    >
      <span className="w-4 shrink-0 text-right text-xs font-medium text-white/40">{rank}</span>

      <span className="flex size-4 shrink-0 items-center justify-center">
        {leader.team?.logo_url && <img src={leader.team.logo_url} alt="" className="size-4 object-contain" />}
      </span>

      <Link
        href={`/players/${leader.player_id}`}
        onClick={(e) => e.stopPropagation()}
        className="white-row-link w-32 shrink-0 truncate text-sm no-underline hover:underline sm:w-40"
      >
        {displayName(leader.player_name)}
      </Link>

      <span className="relative flex min-w-0 flex-1 items-center sm:gap-1.5">
        <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10 sm:h-1.5">
          <span
            className="block h-full rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${barWidth}%`, backgroundColor: barColor }}
          />
        </span>
        <span
          className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-sm font-semibold text-white sm:static sm:top-auto sm:shrink-0 sm:translate-y-0 sm:pl-0 sm:text-right sm:text-base"
          style={{ left: `${barWidth}%` }}
        >
          {formatValue(stat, leader.stat_value)}
        </span>
      </span>
    </div>
  )
}

// A thin vertical bar for every match the player featured in this
// league/season, in chronological order — height = the stat value that
// match, colour by the player's team's result. Hover/tap a bar to pin the
// match score above the chart.
function StatMatchChart({
  playerId,
  leagueId,
  leagueIds,
  season,
  stat,
  enabled,
}: {
  playerId: number
  leagueId: number
  leagueIds?: number[]
  season: number
  stat: string
  enabled: boolean
}) {
  const [activeId, setActiveId] = useState<number | null>(null)

  const { data, isLoading } = useFetchPlayerStatMatches({
    player_id: playerId,
    league_id: leagueId,
    league_ids: leagueIds,
    season_year: season,
    stat,
    limit: 80,
    enabled,
  })

  if (!enabled) return null

  if (isLoading) {
    return (
      <div className="flex h-28 items-center justify-center">
        <Spinner className="size-4 text-white/40" />
      </div>
    )
  }

  const matches: StatMatch[] = data?.data ?? []
  if (matches.length === 0) {
    return <p className="py-2 text-center text-[11px] text-white/40">No match data.</p>
  }

  const maxValue = Math.max(1, ...matches.map((m) => m.stat_value ?? 0))
  const active = matches.find((m) => m.match_id === activeId) ?? null
  // Decimal stats (xG, xA, xGA...) print 4-character labels like "0.87" on
  // top of each bar — give them more breathing room than integer stats.
  const isDecimal = DECIMAL_STATS.has(stat)
  const statLabel = STATS.find((s) => s.value === stat)?.label ?? stat
  const isGoalInvolvements = stat === 'goals_assists'

  return (
    <div className="flex w-full flex-col gap-1.5">
      {/* Pinned detail for the hovered / tapped bar */}
      <div className="flex min-h-7 flex-wrap items-center justify-center gap-1.5 text-[11px]">
        {active ? (
          <>
            {active.home_team?.logo_url && <img src={active.home_team.logo_url} alt="" className="size-4 object-contain" />}
            <span className="text-sm font-semibold tabular-nums text-white">
              {active.home_goals ?? '–'}-{active.away_goals ?? '–'}
            </span>
            {active.away_team?.logo_url && <img src={active.away_team.logo_url} alt="" className="size-4 object-contain" />}
            <span className="text-white/40">· {active.match_date ? formatMonthDay(active.match_date) : ''}</span>
            {isGoalInvolvements ? (
              <span className="flex items-center gap-1 text-sm">
                {(active.goals ?? 0) === 0 && (active.assists ?? 0) === 0 && (
                  <span className="text-white/40">No goals or assists</span>
                )}
                {Array.from({ length: active.goals ?? 0 }).map((_, i) => (
                  <span key={`g${i}`} title="Goal">⚽</span>
                ))}
                {Array.from({ length: active.assists ?? 0 }).map((_, i) => (
                  <span key={`a${i}`} title="Assist">🅰️</span>
                ))}
              </span>
            ) : (
              <span className="text-sm font-semibold text-white">
                {statLabel} {formatBarValue(active.stat_value ?? 0)}
              </span>
            )}
          </>
        ) : (
          <span className="text-white/25">Hover or tap a bar for the match score</span>
        )}
      </div>

      {/* Bars are a constant size for every player. The row shrink-wraps
          (inline-flex) so it centres when it fits and scrolls sideways
          when it doesn't — `text-center` on the scroll box handles the
          centring, which is more reliable on mobile than `mx-auto`. */}
      <div className="w-full overflow-x-auto overscroll-x-contain text-center">
        <div
          className={cn(
            'inline-flex items-end border-b border-white/10 align-bottom',
            isDecimal ? 'gap-1.5' : 'gap-px'
          )}
        >
          {matches.map((m) => {
            const value = m.stat_value ?? 0
            const heightPct = value > 0 ? Math.max((value / maxValue) * 100, 10) : 0
            const isActive = m.match_id === activeId
            return (
              <button
                key={m.match_id}
                type="button"
                title={`${m.home_goals ?? '–'}-${m.away_goals ?? '–'}`}
                onClick={() => setActiveId(isActive ? null : m.match_id)}
                onMouseEnter={() => setActiveId(m.match_id)}
                onMouseLeave={() => setActiveId((cur) => (cur === m.match_id ? null : cur))}
                className={cn('flex flex-none flex-col items-center gap-1', isDecimal ? 'w-7' : 'w-5')}
              >
                {/* Fixed-height bar track so every bar sits on the same
                    baseline regardless of its value label. */}
                <div className="flex h-28 w-full flex-col items-center justify-end">
                  {value > 0 && (
                    <span className="mb-0.5 text-[13px] font-semibold leading-none text-white/80 sm:text-[9px]">{formatBarValue(value)}</span>
                  )}
                  <div
                    style={{ height: value > 0 ? `${heightPct}%` : undefined }}
                    className={cn(
                      'w-3 rounded-none border transition-all duration-200 ease-out',
                      m.result ? OUTCOME_CLASSES[m.result] : 'border-white/15 bg-white/10',
                      // 0-stat games still get a small coloured stub so the
                      // win/loss/draw is readable.
                      value === 0 && 'h-1.5',
                      isActive && 'ring-1 ring-white'
                    )}
                  />
                </div>
                {m.opponent?.logo_url ? (
                  <img src={m.opponent.logo_url} alt="" className="size-5 shrink-0 object-contain" />
                ) : (
                  <div className="size-5 shrink-0 rounded-full bg-white/15" />
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function LeaderDetail({
  leader,
  leagueId,
  leagueIds,
  season,
  stat,
  open,
}: {
  leader: StatsLeader
  leagueId: number
  leagueIds?: number[]
  season: number
  stat: string
  open: boolean
}) {
  const positions = (leader.positions ?? []).map(abbreviatePosition).filter(Boolean)
  const showChart = CHART_STATS.has(stat)

  return (
    <div className="flex flex-col gap-2 px-1 text-xs text-white/60">
      <div className="flex flex-wrap items-center gap-1.5">
        {leader.country?.flag_url ? (
          <img src={leader.country.flag_url} alt="" className="h-[11px] w-[16px] rounded-[1px] object-cover" />
        ) : (
          <div className="h-[11px] w-[16px] rounded-[1px] bg-muted" />
        )}
        <span className="text-white/70">Age: {leader.age ?? '—'}</span>
        <span className="text-white/25">-</span>
        <span className="text-white/70">{leader.gp ?? '—'} GP</span>
        {positions.length > 0 && (
          <>
            <span className="text-white/25">-</span>
            <span className="text-white/70">{positions.join(', ')}</span>
          </>
        )}
      </div>

      {showChart && (
        <StatMatchChart
          playerId={leader.player_id}
          leagueId={leagueId}
          leagueIds={leagueIds}
          season={season}
          stat={stat}
          enabled={open}
        />
      )}
    </div>
  )
}

export function StatsLeaders() {
  const [leagueId, setLeagueId] = useState(DEFAULT_LEAGUE_ID)
  const [season, setSeason] = useState(DEFAULT_SEASON)
  const [stat, setStat] = useState('goals')
  const [position, setPosition] = useState<string | null>(null)
  const [ageMax, setAgeMax] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openPlayer, setOpenPlayer] = useState('')

  // Collapse any open accordion row whenever the query behind the list
  // changes — the row indices no longer line up with the new data.
  useEffect(() => {
    setOpenPlayer('')
  }, [leagueId, season, stat, position, ageMax])

  // "Top 5 Leagues" / "All European Comps" are synthetic picks — send a
  // real (ignored) path league_id plus a league_ids list so the API
  // aggregates across all of them.
  const aggregateLeagueIds =
    leagueId === TOP5_LEAGUE_ID ? TOP5_LEAGUE_IDS
    : leagueId === ALL_EURO_LEAGUE_ID ? ALL_EURO_LEAGUE_IDS
    : undefined
  const apiLeagueId = aggregateLeagueIds ? aggregateLeagueIds[0] : leagueId
  const apiLeagueIds = aggregateLeagueIds

  const { data, isLoading, isError } = useFetchLeagueStatsLeaders({
    league_id: apiLeagueId,
    league_ids: apiLeagueIds,
    season_year: season,
    stat,
    limit: LIMIT,
    position: position ?? undefined,
    age: ageMax ?? undefined,
  })

  const leaders = data?.data.players ?? []
  const maxValue = Math.max(1, ...leaders.map((l) => Math.abs(l.stat_value ?? 0)))
  const statLabel = STATS.find((s) => s.value === stat)?.label ?? 'Goals'

  return (
    <div className="w-full">
      <div className="w-full space-y-3 rounded-xl border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="flex items-center gap-1.5 text-lg font-semibold text-white">
                {statLabel}
                <ChevronDown className="size-4 text-white/50" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-1">
              <div className="flex max-h-80 flex-col overflow-y-auto">
                {STATS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => {
                      setStat(s.value)
                      setMenuOpen(false)
                    }}
                    className={cn(
                      'rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                      stat === s.value ? 'font-medium text-white' : 'text-foreground'
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Select value={String(season)} onValueChange={(v) => setSeason(Number(v))}>
            <SelectTrigger className="w-[90px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[260px] overflow-y-auto">
              {YEAR_OPTIONS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <LeagueSelect value={leagueId} onChange={setLeagueId} />
          </div>

          {/* Age sits directly under position, on every screen size. */}
          <div className="flex shrink-0 flex-col gap-1.5">
            <div className="flex items-center rounded-md border border-white/10 p-0.5">
              {POSITION_FILTERS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setPosition(p.value)}
                  className={cn(
                    'rounded px-2 py-1 text-xs font-medium transition-colors',
                    position === p.value ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center rounded-md border border-white/10 p-0.5">
              {AGE_FILTERS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => setAgeMax(a.value)}
                  className={cn(
                    'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                    ageMax === a.value ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  )}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Break out of the app shell's mobile side padding so the rows run
          edge-to-edge on phones (per the design system); reset at sm+. */}
      <div className="mt-3 -mx-3 sm:mx-0">
        {isLoading ? (
          <LeadersSkeleton />
        ) : isError ? (
          <p className="py-6 text-center text-sm text-red-400">Failed to load stats.</p>
        ) : leaders.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No stats available for this league/season.
          </p>
        ) : (
          <Accordion
            type="single"
            collapsible
            value={openPlayer}
            onValueChange={setOpenPlayer}
            key={`${leagueId}-${season}-${stat}-${position}-${ageMax}`}
          >
            {leaders.map((leader, i) => (
              <AccordionItem
                key={leader.player_id}
                value={String(leader.player_id)}
                className="border-b border-white/10 last:border-b-0"
              >
                <AccordionTrigger className="gap-0 rounded-none px-2 py-2 items-center hover:no-underline hover:bg-white/5 [&>svg]:hidden">
                  <LeaderRowContent leader={leader} rank={i + 1} stat={stat} maxValue={maxValue} delayMs={i * 30} />
                </AccordionTrigger>
                <AccordionContent className="px-3">
                  <LeaderDetail
                    leader={leader}
                    leagueId={apiLeagueId}
                    leagueIds={apiLeagueIds}
                    season={season}
                    stat={stat}
                    open={openPlayer === String(leader.player_id)}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </div>
  )
}
